// =============================================================================
// INSTAGRAM STORIES SHARE CARD GENERATOR - 1080×1920 Vertical Format
// =============================================================================

import { COLORS, STORIES_DIMENSIONS } from './canvas/config';
import { loadLogo } from './canvas/utils';

// Stories dimensions (9:16 ratio for Instagram Stories)
const { width: STORIES_WIDTH, height: STORIES_HEIGHT } = STORIES_DIMENSIONS;

// =============================================================================
// TYPE DEFINITIONS
// =============================================================================

export interface GameStateData {
  turn: number;
  maxTurns: number;
  date: string;
  location: string;
  militaryStrength: number;
  economy: number;
  resilience: number;
  westernSupport: number;
  trust: number;
  // Change indicators (optional)
  militaryChange?: number;
  economyChange?: number;
  resilienceChange?: number;
  westernSupportChange?: number;
  trustChange?: number;
}

export interface DecisionData {
  situation: string;
  chosenOption: string;
  consequences: Array<{
    text: string;
    type: 'success' | 'warning' | 'failure' | 'neutral';
  }>;
  criticality: 'normal' | 'important' | 'critical';
}

export type StoriesTemplate = 'decision' | 'dashboard' | 'critical';

export interface StoriesCardConfig {
  template: StoriesTemplate;
  gameState: GameStateData;
  decision?: DecisionData;
}

// =============================================================================
// MAIN GENERATOR
// =============================================================================

export async function generateStoriesCard(
  config: StoriesCardConfig,
): Promise<string> {
  const canvas = document.createElement('canvas');
  canvas.width = STORIES_WIDTH;
  canvas.height = STORIES_HEIGHT;
  const ctx = canvas.getContext('2d');

  if (!ctx) {
    throw new Error('Canvas context not available');
  }

  // Draw based on template
  switch (config.template) {
    case 'decision':
      await drawDecisionTemplate(ctx, config);
      break;
    case 'dashboard':
      await drawDashboardTemplate(ctx, config);
      break;
    case 'critical':
      await drawCriticalTemplate(ctx, config);
      break;
  }

  return canvas.toDataURL('image/png', 0.92);
}

// =============================================================================
// TEMPLATE A: DECISION IMPACT
// =============================================================================

async function drawDecisionTemplate(
  ctx: CanvasRenderingContext2D,
  config: StoriesCardConfig,
): Promise<void> {
  const { gameState, decision } = config;

  // Background
  drawGradientBackground(ctx, COLORS.background, '#0f1a1a');

  // Scan lines overlay
  drawScanLines(ctx);

  // Top section: Logo and header
  const logo = await loadLogo();
  if (logo) {
    ctx.globalAlpha = 0.6;
    ctx.drawImage(logo, (STORIES_WIDTH - 80) / 2, 60, 80, 80);
    ctx.globalAlpha = 1;
  }

  // Header bar
  drawHeaderBar(ctx, 160, `ХІД ${gameState.turn}/${gameState.maxTurns}`);

  // Date and location
  ctx.font = '500 24px "SF Mono", monospace';
  ctx.fillStyle = COLORS.textDim;
  ctx.textAlign = 'center';
  ctx.fillText(gameState.date, STORIES_WIDTH / 2, 260);
  ctx.font = '400 20px "SF Mono", monospace';
  ctx.fillText(gameState.location, STORIES_WIDTH / 2, 295);

  // Situation section
  drawSectionHeader(ctx, 380, '⚡ СИТУАЦІЯ');

  if (decision?.situation) {
    ctx.font = 'italic 28px "SF Pro Display", Georgia, serif';
    ctx.fillStyle = COLORS.text;
    ctx.textAlign = 'center';
    const situationLines = wrapText(
      ctx,
      decision.situation,
      STORIES_WIDTH - 120,
    );
    let y = 450;
    for (const line of situationLines) {
      ctx.fillText(line, STORIES_WIDTH / 2, y);
      y += 40;
    }
  }

  // Decision section
  drawSectionHeader(ctx, 650, '🎯 ВАШЕ РІШЕННЯ');

  if (decision?.chosenOption) {
    // Decision box
    const boxY = 720;
    const boxHeight = 120;
    drawGlassBox(ctx, 60, boxY, STORIES_WIDTH - 120, boxHeight, COLORS.primary);

    ctx.font = 'bold 26px "SF Pro Display", sans-serif';
    ctx.fillStyle = COLORS.text;
    ctx.textAlign = 'center';
    const decisionLines = wrapText(
      ctx,
      decision.chosenOption,
      STORIES_WIDTH - 160,
    );
    let y = boxY + 50;
    for (const line of decisionLines.slice(0, 2)) {
      ctx.fillText(line, STORIES_WIDTH / 2, y);
      y += 36;
    }
  }

  // Consequences section
  drawSectionHeader(ctx, 920, '📊 НАСЛІДКИ');

  if (decision?.consequences) {
    let y = 990;
    for (const consequence of decision.consequences.slice(0, 4)) {
      const icon = getConsequenceIcon(consequence.type);
      const color = getConsequenceColor(consequence.type);

      ctx.font = '24px "Apple Color Emoji", sans-serif';
      ctx.fillText(icon, 80, y);

      ctx.font = '500 22px "SF Pro Display", sans-serif';
      ctx.fillStyle = color;
      ctx.textAlign = 'left';
      const lines = wrapText(ctx, consequence.text, STORIES_WIDTH - 180);
      ctx.fillText(lines[0], 120, y);
      y += 70;
    }
  }

  // State indicators at bottom
  drawMiniStats(ctx, gameState, 1350);

  // Corner accents
  drawCornerAccents(ctx, COLORS.primary);

  // Bottom branding
  drawStoriesBranding(ctx);

  // Warning stripes
  drawWarningStripes(ctx);
}

// =============================================================================
// TEMPLATE B: STATE DASHBOARD
// =============================================================================

async function drawDashboardTemplate(
  ctx: CanvasRenderingContext2D,
  config: StoriesCardConfig,
): Promise<void> {
  const { gameState } = config;

  // Background
  drawGradientBackground(ctx, COLORS.background, '#0a1214');

  // Scan lines
  drawScanLines(ctx);

  // Logo
  const logo = await loadLogo();
  if (logo) {
    ctx.globalAlpha = 0.7;
    ctx.drawImage(logo, (STORIES_WIDTH - 100) / 2, 80, 100, 100);
    ctx.globalAlpha = 1;
  }

  // Main title
  ctx.font = 'bold 48px "SF Pro Display", sans-serif';
  ctx.fillStyle = COLORS.primary;
  ctx.textAlign = 'center';
  ctx.fillText('СТАН ДЕРЖАВИ', STORIES_WIDTH / 2, 260);

  // Turn indicator
  drawTurnBadge(ctx, gameState.turn, gameState.maxTurns, 320);

  // Date
  ctx.font = '500 22px "SF Mono", monospace';
  ctx.fillStyle = COLORS.textDim;
  ctx.fillText(gameState.date, STORIES_WIDTH / 2, 400);

  // Stats section
  const stats = [
    {
      label: 'ВІЙСЬКОВА СИЛА',
      value: gameState.militaryStrength,
      emoji: '🪖',
      change: gameState.militaryChange,
    },
    {
      label: 'ЕКОНОМІКА',
      value: gameState.economy,
      emoji: '💰',
      change: gameState.economyChange,
    },
    {
      label: 'СТІЙКІСТЬ',
      value: gameState.resilience,
      emoji: '💪',
      change: gameState.resilienceChange,
    },
    {
      label: 'ЗАХІДНА ПІДТРИМКА',
      value: gameState.westernSupport,
      emoji: '🤝',
      change: gameState.westernSupportChange,
    },
    {
      label: 'ДОВІРА НАРОДУ',
      value: gameState.trust,
      emoji: '🏛️',
      change: gameState.trustChange,
    },
  ];

  let y = 500;
  for (const stat of stats) {
    drawFullWidthStat(ctx, stat, y);
    y += 180;
  }

  // Average score
  const avgScore = Math.round(
    (gameState.militaryStrength +
      gameState.economy +
      gameState.resilience +
      gameState.westernSupport +
      gameState.trust) /
    5,
  );

  drawScoreCircle(ctx, avgScore, STORIES_WIDTH / 2, 1550);

  // Corner accents
  drawCornerAccents(ctx, COLORS.primary);

  // Bottom branding
  drawStoriesBranding(ctx);

  // Warning stripes
  drawWarningStripes(ctx);
}

// =============================================================================
// TEMPLATE C: CRITICAL MOMENT
// =============================================================================

async function drawCriticalTemplate(
  ctx: CanvasRenderingContext2D,
  config: StoriesCardConfig,
): Promise<void> {
  const { gameState, decision } = config;

  // Dramatic red-tinted background
  drawGradientBackground(ctx, '#0a0a0a', '#1a0808');

  // Heavy scan lines for drama
  drawScanLines(ctx, 0.05);

  // Red glow effect
  const gradient = ctx.createRadialGradient(
    STORIES_WIDTH / 2,
    600,
    100,
    STORIES_WIDTH / 2,
    600,
    600,
  );
  gradient.addColorStop(0, 'rgba(239, 68, 68, 0.15)');
  gradient.addColorStop(1, 'transparent');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, STORIES_WIDTH, STORIES_HEIGHT);

  // "CRITICAL" badge at top
  ctx.fillStyle = COLORS.critical;
  ctx.fillRect(0, 80, STORIES_WIDTH, 80);

  ctx.font = 'bold 40px "SF Pro Display", Impact, sans-serif';
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.fillText('🔥 КРИТИЧНИЙ МОМЕНТ', STORIES_WIDTH / 2, 135);

  // Turn indicator
  ctx.font = 'bold 28px "SF Mono", monospace';
  ctx.fillStyle = COLORS.textDim;
  ctx.fillText(`ХІД ${gameState.turn}`, STORIES_WIDTH / 2, 220);

  // Large decision quote - the centerpiece
  if (decision?.chosenOption) {
    // Quote marks
    ctx.font = 'bold 200px Georgia, serif';
    ctx.fillStyle = COLORS.critical;
    ctx.globalAlpha = 0.15;
    ctx.textAlign = 'left';
    ctx.fillText('"', 40, 450);
    ctx.textAlign = 'right';
    ctx.fillText('"', STORIES_WIDTH - 40, 1100);
    ctx.globalAlpha = 1;

    // Main decision text
    ctx.font = 'bold 44px "SF Pro Display", sans-serif';
    ctx.fillStyle = COLORS.text;
    ctx.textAlign = 'center';

    const lines = wrapText(ctx, decision.chosenOption, STORIES_WIDTH - 140);
    const lineHeight = 60;
    const startY = (STORIES_HEIGHT - lines.length * lineHeight) / 2 - 100;

    ctx.shadowColor = COLORS.critical;
    ctx.shadowBlur = 20;
    for (let i = 0; i < Math.min(lines.length, 6); i++) {
      ctx.fillText(lines[i], STORIES_WIDTH / 2, startY + i * lineHeight);
    }
    ctx.shadowBlur = 0;
  }

  // Situation context below
  if (decision?.situation) {
    ctx.font = 'italic 24px "SF Pro Display", Georgia, serif';
    ctx.fillStyle = COLORS.textDim;
    const situationLines = wrapText(
      ctx,
      decision.situation,
      STORIES_WIDTH - 160,
    );
    let y = 1150;
    for (const line of situationLines.slice(0, 3)) {
      ctx.fillText(line, STORIES_WIDTH / 2, y);
      y += 35;
    }
  }

  // Key stats
  drawMiniStats(ctx, gameState, 1350);

  // Attribution
  ctx.font = '600 20px "SF Mono", monospace';
  ctx.fillStyle = COLORS.primary;
  ctx.textAlign = 'center';
  ctx.fillText('— ГОЛОВНОКОМАНДУВАЧ', STORIES_WIDTH / 2, 1500);

  // Corner accents (red theme)
  drawCornerAccents(ctx, COLORS.critical);

  // Bottom branding
  drawStoriesBranding(ctx);

  // Warning stripes
  drawWarningStripes(ctx);
}

// =============================================================================
// HELPER DRAWING FUNCTIONS
// =============================================================================

function drawGradientBackground(
  ctx: CanvasRenderingContext2D,
  color1: string,
  color2: string,
): void {
  const gradient = ctx.createLinearGradient(
    0,
    0,
    STORIES_WIDTH,
    STORIES_HEIGHT,
  );
  gradient.addColorStop(0, color1);
  gradient.addColorStop(1, color2);
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, STORIES_WIDTH, STORIES_HEIGHT);
}

function drawScanLines(ctx: CanvasRenderingContext2D, alpha = 0.03): void {
  ctx.globalAlpha = alpha;
  ctx.fillStyle = COLORS.primary;
  for (let y = 0; y < STORIES_HEIGHT; y += 4) {
    ctx.fillRect(0, y, STORIES_WIDTH, 1);
  }
  ctx.globalAlpha = 1;
}

function drawHeaderBar(
  ctx: CanvasRenderingContext2D,
  y: number,
  text: string,
): void {
  // Background bar
  ctx.fillStyle = COLORS.primary;
  ctx.globalAlpha = 0.15;
  ctx.fillRect(60, y, STORIES_WIDTH - 120, 60);
  ctx.globalAlpha = 1;

  // Border
  ctx.strokeStyle = COLORS.primary;
  ctx.lineWidth = 2;
  ctx.strokeRect(60, y, STORIES_WIDTH - 120, 60);

  // Text
  ctx.font = 'bold 24px "SF Mono", monospace';
  ctx.fillStyle = COLORS.primary;
  ctx.textAlign = 'center';
  ctx.fillText(text, STORIES_WIDTH / 2, y + 40);
}

function drawSectionHeader(
  ctx: CanvasRenderingContext2D,
  y: number,
  text: string,
): void {
  ctx.font = 'bold 22px "SF Mono", monospace';
  ctx.fillStyle = COLORS.primary;
  ctx.textAlign = 'left';
  ctx.fillText(text, 60, y);

  // Underline
  ctx.strokeStyle = COLORS.primary;
  ctx.globalAlpha = 0.3;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(60, y + 10);
  ctx.lineTo(STORIES_WIDTH - 60, y + 10);
  ctx.stroke();
  ctx.globalAlpha = 1;
}

function drawGlassBox(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  borderColor: string,
): void {
  // Background
  ctx.fillStyle = 'rgba(16, 185, 129, 0.08)';
  ctx.fillRect(x, y, width, height);

  // Border
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = 2;
  ctx.strokeRect(x, y, width, height);

  // Corner accents
  const corner = 15;
  ctx.lineWidth = 3;

  // Top-left
  ctx.beginPath();
  ctx.moveTo(x, y + corner);
  ctx.lineTo(x, y);
  ctx.lineTo(x + corner, y);
  ctx.stroke();

  // Top-right
  ctx.beginPath();
  ctx.moveTo(x + width - corner, y);
  ctx.lineTo(x + width, y);
  ctx.lineTo(x + width, y + corner);
  ctx.stroke();

  // Bottom-left
  ctx.beginPath();
  ctx.moveTo(x, y + height - corner);
  ctx.lineTo(x, y + height);
  ctx.lineTo(x + corner, y + height);
  ctx.stroke();

  // Bottom-right
  ctx.beginPath();
  ctx.moveTo(x + width - corner, y + height);
  ctx.lineTo(x + width, y + height);
  ctx.lineTo(x + width, y + height - corner);
  ctx.stroke();
}

function drawMiniStats(
  ctx: CanvasRenderingContext2D,
  gameState: GameStateData,
  startY: number,
): void {
  const stats = [
    { label: '🪖', value: gameState.militaryStrength },
    { label: '💰', value: gameState.economy },
    { label: '💪', value: gameState.resilience },
    { label: '🤝', value: gameState.westernSupport },
    { label: '🏛️', value: gameState.trust },
  ];

  const spacing = (STORIES_WIDTH - 120) / stats.length;
  const barWidth = spacing - 20;

  ctx.textAlign = 'center';

  stats.forEach((stat, index) => {
    const x = 60 + spacing * index + spacing / 2;

    // Emoji
    ctx.font = '28px "Apple Color Emoji", sans-serif';
    ctx.fillText(stat.label, x, startY);

    // Progress bar background
    ctx.fillStyle = COLORS.border;
    ctx.fillRect(x - barWidth / 2, startY + 20, barWidth, 12);

    // Progress bar fill
    ctx.fillStyle = getStatColor(stat.value);
    ctx.fillRect(
      x - barWidth / 2,
      startY + 20,
      (barWidth * stat.value) / 100,
      12,
    );

    // Value
    ctx.font = 'bold 18px "SF Mono", monospace';
    ctx.fillStyle = COLORS.text;
    ctx.fillText(`${stat.value}%`, x, startY + 55);
  });
}

function drawFullWidthStat(
  ctx: CanvasRenderingContext2D,
  stat: { label: string; value: number; emoji: string; change?: number },
  y: number,
): void {
  const barX = 80;
  const barWidth = STORIES_WIDTH - 160;
  const barHeight = 40;

  // Emoji
  ctx.font = '40px "Apple Color Emoji", sans-serif';
  ctx.textAlign = 'left';
  ctx.fillText(stat.emoji, barX, y);

  // Label
  ctx.font = '600 20px "SF Mono", monospace';
  ctx.fillStyle = COLORS.textDim;
  ctx.fillText(stat.label, barX + 60, y - 5);

  // Change indicator
  if (stat.change !== undefined && stat.change !== 0) {
    const changeColor = stat.change > 0 ? COLORS.success : COLORS.critical;
    const changeText =
      stat.change > 0 ? `↑ +${stat.change}%` : `↓ ${stat.change}%`;
    ctx.fillStyle = changeColor;
    ctx.textAlign = 'right';
    ctx.fillText(changeText, STORIES_WIDTH - 80, y - 5);
    ctx.textAlign = 'left';
  }

  // Progress bar background
  ctx.fillStyle = COLORS.border;
  ctx.fillRect(barX, y + 20, barWidth, barHeight);

  // Progress bar fill
  const fillColor = getStatColor(stat.value);
  ctx.fillStyle = fillColor;
  ctx.fillRect(barX, y + 20, (barWidth * stat.value) / 100, barHeight);

  // Glow effect on bar
  ctx.shadowColor = fillColor;
  ctx.shadowBlur = 10;
  ctx.fillRect(barX, y + 20, (barWidth * stat.value) / 100, barHeight);
  ctx.shadowBlur = 0;

  // Value text inside bar
  ctx.font = 'bold 24px "SF Pro Display", sans-serif';
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'right';
  ctx.fillText(`${stat.value}%`, STORIES_WIDTH - 100, y + 50);
}

function drawTurnBadge(
  ctx: CanvasRenderingContext2D,
  turn: number,
  maxTurns: number,
  y: number,
): void {
  const badgeWidth = 180;
  const badgeHeight = 50;
  const x = (STORIES_WIDTH - badgeWidth) / 2;

  ctx.fillStyle = COLORS.primary;
  ctx.fillRect(x, y, badgeWidth, badgeHeight);

  ctx.font = 'bold 26px "SF Mono", monospace';
  ctx.fillStyle = '#000000';
  ctx.textAlign = 'center';
  ctx.fillText(`${turn}/${maxTurns}`, STORIES_WIDTH / 2, y + 35);
}

function drawScoreCircle(
  ctx: CanvasRenderingContext2D,
  score: number,
  x: number,
  y: number,
): void {
  const radius = 80;

  // Circle background
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.fillStyle = COLORS.primaryDim;
  ctx.fill();

  // Circle border
  ctx.beginPath();
  ctx.arc(x, y, radius, 0, Math.PI * 2);
  ctx.strokeStyle = COLORS.primary;
  ctx.lineWidth = 4;
  ctx.stroke();

  // Progress arc
  const startAngle = -Math.PI / 2;
  const endAngle = startAngle + (Math.PI * 2 * score) / 100;
  ctx.beginPath();
  ctx.arc(x, y, radius, startAngle, endAngle);
  ctx.strokeStyle = COLORS.primary;
  ctx.lineWidth = 10;
  ctx.stroke();

  // Score text
  ctx.font = 'bold 56px "SF Pro Display", sans-serif';
  ctx.fillStyle = COLORS.text;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(`${score}`, x, y - 8);

  // Percent sign
  ctx.font = 'bold 24px "SF Pro Display", sans-serif';
  ctx.fillStyle = COLORS.primary;
  ctx.fillText('%', x, y + 35);

  ctx.textBaseline = 'alphabetic';
}

function drawCornerAccents(ctx: CanvasRenderingContext2D, color: string): void {
  const size = 60;
  const thickness = 4;
  const margin = 20;

  ctx.strokeStyle = color;
  ctx.lineWidth = thickness;
  ctx.shadowColor = color;
  ctx.shadowBlur = 15;

  // Top-left
  ctx.beginPath();
  ctx.moveTo(margin, margin + size);
  ctx.lineTo(margin, margin);
  ctx.lineTo(margin + size, margin);
  ctx.stroke();

  // Top-right
  ctx.beginPath();
  ctx.moveTo(STORIES_WIDTH - margin - size, margin);
  ctx.lineTo(STORIES_WIDTH - margin, margin);
  ctx.lineTo(STORIES_WIDTH - margin, margin + size);
  ctx.stroke();

  // Bottom-left
  ctx.beginPath();
  ctx.moveTo(margin, STORIES_HEIGHT - margin - size);
  ctx.lineTo(margin, STORIES_HEIGHT - margin);
  ctx.lineTo(margin + size, STORIES_HEIGHT - margin);
  ctx.stroke();

  // Bottom-right
  ctx.beginPath();
  ctx.moveTo(STORIES_WIDTH - margin - size, STORIES_HEIGHT - margin);
  ctx.lineTo(STORIES_WIDTH - margin, STORIES_HEIGHT - margin);
  ctx.lineTo(STORIES_WIDTH - margin, STORIES_HEIGHT - margin - size);
  ctx.stroke();

  ctx.shadowBlur = 0;
}

function drawStoriesBranding(ctx: CanvasRenderingContext2D): void {
  // Bottom gradient overlay
  const gradient = ctx.createLinearGradient(
    0,
    STORIES_HEIGHT - 200,
    0,
    STORIES_HEIGHT,
  );
  gradient.addColorStop(0, 'transparent');
  gradient.addColorStop(1, 'rgba(0, 0, 0, 0.6)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, STORIES_HEIGHT - 200, STORIES_WIDTH, 200);

  // CTA
  ctx.font = 'bold 26px "SF Pro Display", sans-serif';
  ctx.fillStyle = COLORS.text;
  ctx.textAlign = 'center';
  ctx.fillText('ПРИЙМИ КОМАНДУВАННЯ', STORIES_WIDTH / 2, STORIES_HEIGHT - 120);

  // URL
  ctx.font = '600 20px "SF Mono", monospace';
  ctx.fillStyle = COLORS.primary;
  ctx.fillText(
    '🎮 holovkom.zavoruha.tech',
    STORIES_WIDTH / 2,
    STORIES_HEIGHT - 80,
  );

  // Hashtags
  ctx.font = '500 16px "SF Mono", monospace';
  ctx.fillStyle = COLORS.textDim;
  ctx.fillText(
    '#Головнокомандувач #ЗСУ #Україна',
    STORIES_WIDTH / 2,
    STORIES_HEIGHT - 45,
  );
}

function drawWarningStripes(ctx: CanvasRenderingContext2D): void {
  const stripeHeight = 10;
  const stripeWidth = 24;

  ctx.fillStyle = COLORS.warning;

  // Top stripe
  for (let x = 0; x < STORIES_WIDTH; x += stripeWidth * 2) {
    ctx.fillRect(x, 0, stripeWidth, stripeHeight);
  }

  // Bottom stripe
  for (let x = stripeWidth; x < STORIES_WIDTH; x += stripeWidth * 2) {
    ctx.fillRect(x, STORIES_HEIGHT - stripeHeight, stripeWidth, stripeHeight);
  }
}

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine + word + ' ';
    const metrics = ctx.measureText(testLine);
    if (metrics.width > maxWidth && currentLine !== '') {
      lines.push(currentLine.trim());
      currentLine = word + ' ';
    } else {
      currentLine = testLine;
    }
  }
  lines.push(currentLine.trim());

  return lines;
}

function getStatColor(value: number): string {
  if (value >= 70) return COLORS.success;
  if (value >= 40) return COLORS.warning;
  return COLORS.critical;
}

function getConsequenceIcon(type: string): string {
  switch (type) {
    case 'success':
      return '✓';
    case 'warning':
      return '⚠';
    case 'failure':
      return '✗';
    default:
      return '→';
  }
}

function getConsequenceColor(type: string): string {
  switch (type) {
    case 'success':
      return COLORS.success;
    case 'warning':
      return COLORS.warning;
    case 'failure':
      return COLORS.critical;
    default:
      return COLORS.textDim;
  }
}

// =============================================================================
// SHARE FUNCTIONS
// =============================================================================

/**
 * Download Stories card as PNG
 */
export async function downloadStoriesCard(
  config: StoriesCardConfig,
): Promise<void> {
  const dataUrl = await generateStoriesCard(config);
  const link = document.createElement('a');
  link.download = `holovkom-stories-${config.template}-${config.gameState.turn}.png`;
  link.href = dataUrl;
  link.click();
}

/**
 * Share Stories card using native share with file
 */
export async function shareStoriesNative(
  config: StoriesCardConfig,
  shareText: string,
): Promise<boolean> {
  if (!navigator.share || !navigator.canShare) {
    return false;
  }

  try {
    const dataUrl = await generateStoriesCard(config);
    const response = await fetch(dataUrl);
    const blob = await response.blob();
    const file = new File([blob], 'holovkom-stories.png', {
      type: 'image/png',
    });

    const shareData = {
      files: [file],
      title: 'Головнокомандувач',
      text: shareText,
    };

    if (navigator.canShare(shareData)) {
      await navigator.share(shareData);
      return true;
    }

    // Fallback to text-only
    await navigator.share({ title: 'Головнокомандувач', text: shareText });
    return true;
  } catch (err) {
    if ((err as Error).name !== 'AbortError') {
      console.error('Stories share failed:', err);
    }
    return false;
  }
}

/**
 * Copy Stories card to clipboard
 */
export async function copyStoriesCard(
  config: StoriesCardConfig,
): Promise<boolean> {
  if (!navigator.clipboard?.write) {
    return false;
  }

  try {
    const dataUrl = await generateStoriesCard(config);
    const response = await fetch(dataUrl);
    const blob = await response.blob();

    await navigator.clipboard.write([
      new ClipboardItem({
        [blob.type]: blob,
      }),
    ]);

    return true;
  } catch (err) {
    console.error('Failed to copy stories card:', err);
    return false;
  }
}

/**
 * Generate share text based on template
 */
export function generateStoriesShareText(config: StoriesCardConfig): string {
  const { template, gameState } = config;
  const avgScore = Math.round(
    (gameState.militaryStrength +
      gameState.economy +
      gameState.resilience +
      gameState.westernSupport +
      gameState.trust) /
    5,
  );

  switch (template) {
    case 'decision':
      return `⚡ Щойно прийняв критичне рішення! Хід #${gameState.turn} змінив хід війни.\n\n🎮 holovkom.zavoruha.tech\n\n#Головнокомандувач #ЗСУ`;

    case 'dashboard':
      return `📊 Стан держави після ${gameState.turn} ходів:\n🪖 Військо: ${gameState.militaryStrength}%\n💪 Стійкість: ${gameState.resilience}%\n📈 Загалом: ${avgScore}%\n\nКожне рішення має наслідки.\n\n🎮 holovkom.zavoruha.tech\n\n#Головнокомандувач`;

    case 'critical':
      return `🔥 КРИТИЧНИЙ МОМЕНТ!\n\nХід #${gameState.turn} — все на кону.\n\n🎮 holovkom.zavoruha.tech\n\n#Головнокомандувач #УкраїнаПереможе`;

    default:
      return `🎮 Прийми командування!\n\nholovkom.zavoruha.tech\n\n#Головнокомандувач`;
  }
}
